import React, { useState, useEffect } from "react";
import {
  Database,
  Search,
  Plus,
  RefreshCw,
  FileText,
  CheckCircle2,
  AlertCircle,
  Clock,
  ExternalLink,
  Copy,
  Check,
  Filter,
  ShieldCheck,
  Layers,
  HardDrive
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/lib/supabase/client";

interface IngestedDoc {
  id?: string;
  canonical_name: string;
  document_title: string;
  document_type: string;
  sha256_hash?: string;
  storage_bucket?: string;
  storage_path?: string;
  storage_url?: string;
  chunk_count?: number;
  status: "completed" | "processing" | "failed";
  source_url?: string;
  created_at?: string;
  updated_at?: string;
  error?: string;
}

export default function AdminIngestRegistry() {
  const { toast } = useToast();
  const [documents, setDocuments] = useState<IngestedDoc[]>([]);
  const [stats, setStats] = useState<{
    total_documents: number;
    completed_documents: number;
    total_chunks: number;
    storage_bucket: string;
    vector_bucket: string;
  }>({
    total_documents: 0,
    completed_documents: 0,
    total_chunks: 0,
    storage_bucket: "raw_documents",
    vector_bucket: "legal_vectors",
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [copiedHash, setCopiedHash] = useState<string | null>(null);

  // Modal State for On-Demand Ingestion
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [newEntityName, setNewEntityName] = useState<string>("");
  const [newCustomUrl, setNewCustomUrl] = useState<string>("");
  const [newDocType, setNewDocType] = useState<string>("ley");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const fetchRegistry = async () => {
    setIsLoading(true);
    try {
      const backendUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:8001";
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const res = await fetch(`${backendUrl}/api/v1/ingest/registry?limit=100`, {
        headers: {
          Authorization: `Bearer ${token || ""}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        setDocuments(data.registry || []);
        if (data.stats) {
          setStats(data.stats);
        }
      } else {
        toast({
          title: "Error al consultar registro",
          description: "No se pudo obtener la lista de documentos ingestados.",
          variant: "destructive",
        });
      }
    } catch (err) {
      console.error("Error fetching registry:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRegistry();
  }, []);

  const handleCopyHash = (hash: string) => {
    navigator.clipboard.writeText(hash);
    setCopiedHash(hash);
    setTimeout(() => setCopiedHash(null), 2000);
    toast({
      title: "Hash copiado",
      description: "Hash SHA-256 copiado al portapapeles.",
    });
  };

  const handleTriggerIngest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEntityName.trim()) {
      toast({
        title: "Nombre requerido",
        description: "Ingresa el nombre o número de la norma/sentencia.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const backendUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:8001";
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const res = await fetch(`${backendUrl}/api/v1/ingest/web-demand`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token || ""}`,
        },
        body: JSON.stringify({
          entity_name: newEntityName.trim(),
          custom_url: newCustomUrl.trim() || undefined,
          document_type: newDocType,
          run_in_background: true,
        }),
      });

      const result = await res.json();

      if (res.ok) {
        toast({
          title: "Ingesta Iniciada",
          description: result.message || `Procesando '${newEntityName}' en segundo plano.`,
        });
        setIsModalOpen(false);
        setNewEntityName("");
        setNewCustomUrl("");
        fetchRegistry();
      } else {
        toast({
          title: "Error en ingesta",
          description: result.detail || "No se pudo iniciar la ingesta del documento.",
          variant: "destructive",
        });
      }
    } catch (err: any) {
      toast({
        title: "Error de conexión",
        description: err.message || "Error al comunicarse con el worker de ingesta.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filtered documents
  const filteredDocs = documents.filter((doc) => {
    const matchesSearch =
      doc.canonical_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (doc.document_title || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (doc.sha256_hash || "").toLowerCase().includes(searchQuery.toLowerCase());

    const matchesType = typeFilter === "all" || doc.document_type === typeFilter;

    return matchesSearch && matchesType;
  });

  const totalChunks = documents.reduce((acc, curr) => acc + (curr.chunk_count || 0), 0);
  const completedDocs = documents.filter((d) => d.status === "completed").length;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 animate-fade-in">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl">
              <Database className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
                Control de Ingestas & Base Vectorial RAG
              </h1>
              <p className="text-sm text-slate-500">
                Auditoría de archivos en <span className="font-semibold text-slate-700">raw_documents</span>, deduplicación SHA-256 e indexación en <span className="font-semibold text-slate-700">legal_vectors</span>.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchRegistry}
            disabled={isLoading}
            className="rounded-xl border-slate-200 hover:bg-slate-50 text-slate-700 h-10 px-3.5"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin text-indigo-600" : ""}`} />
            Actualizar
          </Button>

          <Button
            onClick={() => setIsModalOpen(true)}
            className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-md shadow-indigo-600/20 h-10 px-4"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Ingestar Nueva Norma
          </Button>
        </div>
      </div>

      {/* KPI Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Documentos Registrados</p>
            <h3 className="text-2xl font-extrabold text-slate-900 mt-1">
              {(stats.total_documents || documents.length).toLocaleString()}
            </h3>
            <span className="text-xs text-emerald-600 font-medium flex items-center gap-1 mt-1">
              <CheckCircle2 className="h-3.5 w-3.5" /> {(stats.completed_documents || completedDocs).toLocaleString()} Completados
            </span>
          </div>
          <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
            <FileText className="h-6 w-6" />
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Total Chunks Vectorizados</p>
            <h3 className="text-2xl font-extrabold text-indigo-600 mt-1">
              {(stats.total_chunks || totalChunks).toLocaleString()}
            </h3>
            <span className="text-xs text-slate-500 mt-1 block">Índice: mistral-1024-v2</span>
          </div>
          <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
            <Layers className="h-6 w-6" />
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Bucket de Almacenamiento</p>
            <h3 className="text-lg font-bold text-slate-900 mt-1">raw_documents</h3>
            <span className="text-xs text-emerald-600 font-medium flex items-center gap-1 mt-1">
              <ShieldCheck className="h-3.5 w-3.5" /> Deduplicación SHA-256
            </span>
          </div>
          <div className="p-3 bg-amber-50 text-amber-600 rounded-xl">
            <HardDrive className="h-6 w-6" />
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Control Anti-Duplicidad</p>
            <h3 className="text-lg font-bold text-emerald-600 mt-1">3 Niveles Activo</h3>
            <span className="text-xs text-slate-500 mt-1 block">Evita re-indexación duplicada</span>
          </div>
          <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
            <ShieldCheck className="h-6 w-6" />
          </div>
        </div>
      </div>

      {/* Filters and Search Bar */}
      <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex flex-col md:flex-row items-center justify-between gap-3">
        <div className="relative w-full md:w-96">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Buscar por nombre, título o SHA-256..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-10 rounded-xl border-slate-200 focus-visible:ring-indigo-500"
          />
        </div>

        <div className="flex items-center gap-2 w-full md:w-auto">
          <Filter className="h-4 w-4 text-slate-400 hidden md:block" />
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full md:w-44 h-10 rounded-xl border-slate-200 text-slate-700">
              <SelectValue placeholder="Tipo de Documento" />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              <SelectItem value="all">Todos los Tipos</SelectItem>
              <SelectItem value="ley">Leyes</SelectItem>
              <SelectItem value="decreto">Decretos</SelectItem>
              <SelectItem value="sentencia">Sentencias</SelectItem>
              <SelectItem value="codigo">Códigos</SelectItem>
              <SelectItem value="general">General</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Registry Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-slate-600">
            <thead className="bg-slate-50/80 text-xs font-semibold uppercase text-slate-500 tracking-wider border-b border-slate-100">
              <tr>
                <th className="py-3.5 px-4">Norma / Documento</th>
                <th className="py-3.5 px-4">Tipo</th>
                <th className="py-3.5 px-4">Hash SHA-256</th>
                <th className="py-3.5 px-4">Chunks</th>
                <th className="py-3.5 px-4">Estado</th>
                <th className="py-3.5 px-4">Archivo raw_documents</th>
                <th className="py-3.5 px-4 text-right">Fecha Ingesta</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-400">
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto text-indigo-600 mb-2" />
                    Cargando registro de ingestas...
                  </td>
                </tr>
              ) : filteredDocs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-400">
                    <Database className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                    No se encontraron documentos registrados con los filtros aplicados.
                  </td>
                </tr>
              ) : (
                filteredDocs.map((doc, idx) => (
                  <tr key={doc.id || doc.canonical_name || idx} className="hover:bg-slate-50/60 transition-colors">
                    {/* Title and Canonical Name */}
                    <td className="py-3.5 px-4">
                      <div className="font-semibold text-slate-900">{doc.document_title || doc.canonical_name}</div>
                      <div className="text-[11px] font-mono text-indigo-600">{doc.canonical_name}</div>
                    </td>

                    {/* Type Badge */}
                    <td className="py-3.5 px-4">
                      <Badge variant="outline" className="capitalize text-slate-700 bg-slate-50 border-slate-200">
                        {doc.document_type || "general"}
                      </Badge>
                    </td>

                    {/* Hash */}
                    <td className="py-3.5 px-4">
                      {doc.sha256_hash ? (
                        <button
                          onClick={() => handleCopyHash(doc.sha256_hash!)}
                          className="flex items-center gap-1.5 font-mono text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-1 rounded-lg transition-colors group"
                          title="Copiar Hash SHA-256"
                        >
                          <span>{doc.sha256_hash.slice(0, 10)}...{doc.sha256_hash.slice(-6)}</span>
                          {copiedHash === doc.sha256_hash ? (
                            <Check className="h-3 w-3 text-emerald-600" />
                          ) : (
                            <Copy className="h-3 w-3 text-slate-400 group-hover:text-slate-700" />
                          )}
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>

                    {/* Chunks */}
                    <td className="py-3.5 px-4 font-semibold text-slate-700">
                      {doc.chunk_count ? (
                        <span className="inline-flex items-center gap-1">
                          <Layers className="h-3.5 w-3.5 text-indigo-500" /> {doc.chunk_count}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="py-3.5 px-4">
                      {doc.status === "completed" && (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50 gap-1">
                          <CheckCircle2 className="h-3 w-3 text-emerald-600" /> Indexado
                        </Badge>
                      )}
                      {doc.status === "processing" && (
                        <Badge className="bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50 gap-1">
                          <Clock className="h-3 w-3 text-amber-600 animate-spin" /> Procesando
                        </Badge>
                      )}
                      {doc.status === "failed" && (
                        <Badge className="bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-50 gap-1" title={doc.error}>
                          <AlertCircle className="h-3 w-3 text-rose-600" /> Fallido
                        </Badge>
                      )}
                    </td>

                    {/* Raw Document Link */}
                    <td className="py-3.5 px-4">
                      {doc.storage_url ? (
                        <a
                          href={doc.storage_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          Ver PDF en Storage <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : doc.storage_path ? (
                        <span className="text-xs text-slate-500 font-mono">{doc.storage_path}</span>
                      ) : (
                        <span className="text-xs text-slate-400">Sin archivo</span>
                      )}
                    </td>

                    {/* Date */}
                    <td className="py-3.5 px-4 text-right text-xs text-slate-500">
                      {doc.created_at ? new Date(doc.created_at).toLocaleDateString("es-CO", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit"
                      }) : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal: Ingest On-Demand */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2 text-slate-900">
              <Plus className="h-5 w-5 text-indigo-600" />
              Ingestar Nueva Norma / Búsqueda Web
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              El worker buscará el texto oficial en repositorios públicos (Senado, SUIN, Corte Constitucional), compilará el archivo a <span className="font-semibold text-slate-700">raw_documents</span> e indexará los fragmentos en <span className="font-semibold text-slate-700">legal_vectors</span>.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleTriggerIngest} className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                Nombre de la Norma o Sentencia *
              </label>
              <Input
                placeholder="Ej: Ley 1564 de 2012, Sentencia C-590 de 2005, Código Civil..."
                value={newEntityName}
                onChange={(e) => setNewEntityName(e.target.value)}
                required
                className="h-10 rounded-xl"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                  Tipo de Documento
                </label>
                <Select value={newDocType} onValueChange={setNewDocType}>
                  <SelectTrigger className="h-10 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectItem value="ley">Ley</SelectItem>
                    <SelectItem value="decreto">Decreto</SelectItem>
                    <SelectItem value="sentencia">Sentencia</SelectItem>
                    <SelectItem value="codigo">Código</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                  URL Oficial (Opcional)
                </label>
                <Input
                  placeholder="https://... (dejar vacío para autodescubrir)"
                  value={newCustomUrl}
                  onChange={(e) => setNewCustomUrl(e.target.value)}
                  className="h-10 rounded-xl text-xs"
                />
              </div>
            </div>

            <DialogFooter className="pt-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                disabled={isSubmitting}
                className="rounded-xl"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl"
              >
                {isSubmitting ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Iniciando Worker...
                  </>
                ) : (
                  "Iniciar Ingesta"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
